import React, { useState, useEffect } from 'react';
import { Steps, Card, Button, message, Typography, Skeleton, Alert } from 'antd';
import { UserOutlined, CalendarOutlined, CreditCardOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import MentorshipAPI from '../../services/MentorshipAPI';
import MentorProfileStep from './steps/MentorProfileStep';

import PaymentStep from './steps/PaymentStep';
import type { Duration, Mentor, MentorshipSession } from '../../models/Mentor';
import ScheduleStep from './steps/ScheduleStep';


const LoadingStep: React.FC = () => (
    <div className="space-y-4">
        <Skeleton.Avatar size={64} active />
        <div className="space-y-2">
            <Skeleton.Input style={{ width: 200 }} active />
            <Skeleton.Input style={{ width: 300 }} active />
            <Skeleton paragraph={{ rows: 3 }} active />
        </div>
    </div>
);

const MentorshipSessionCreate: React.FC = () => {
    const navigate = useNavigate();
    const { mentorUsername } = useParams<{ mentorUsername: string }>();
    const [currentStep, setCurrentStep] = useState(0);
    const [mentor, setMentor] = useState<Mentor | null>(null);
    const [loading, setLoading] = useState(true);
    const [mentorshipSession, setMentorshipSession] = useState<MentorshipSession | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [selectedPrice, setSelectedPrice] = useState<Duration | null>(null);

    useEffect(() => {
        const fetchMentor = async () => {
            try {
                console.log(mentorUsername);
                const response = await MentorshipAPI.listMentors(`?username=${mentorUsername}`);
                console.log(response.data);
                if (response.data.results && response.data.results.length > 0) {
                    console.log(response.data.results[0]);
                    setMentor(response.data.results[0]);
                    setSelectedPrice(response.data.results[0]?.prices[0]);
                } else {
                    setErrorMessage('Mentor not found');
                    navigate('/mentors');
                }
            } catch (error) {
                setErrorMessage('Failed to fetch mentor data');
                navigate('/mentors');
            } finally {
                setLoading(false);
            }
        };

        if (mentorUsername) {
            fetchMentor();
        }
    }, [mentorUsername, navigate]);

    const handleOnSessionCreated = (session: MentorshipSession) => {
        setMentorshipSession(session);
        setCurrentStep(prev => prev + 1);
        setSuccessMessage('Mentorship session created successfully');
    }

    const steps = [
        {
            title: 'Profile',
            icon: <UserOutlined />,
            content: mentor ? <MentorProfileStep mentor={mentor} /> : null,
        },
        {
            title: 'Payment',
            icon: <CreditCardOutlined />,
            content: mentor ? (
                <PaymentStep 
                    mentor={mentor}
                    onSessionCreated={handleOnSessionCreated}
                />
            ) : null,
        },
        {
            title: 'Schedule',
            icon: <CalendarOutlined />,
            content: mentorshipSession ? <ScheduleStep 
                                scheduleURL={selectedPrice?.schedule_url} 
                                mentorshipSession={mentorshipSession}
                                onSessionScheduled={(session: MentorshipSession) => { setMentorshipSession(session)}}
                                  /> : null,
        },
    ];

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto p-4">
                <Card>
                    <Skeleton.Input style={{ width: '100%', height: 40 }} active />
                    <div className="min-h-[400px] mt-8">
                        <LoadingStep />
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-4">
            <Card>
                <Steps current={currentStep} items={steps} className="mb-8" />
                {errorMessage && <Alert message={errorMessage} type="error" showIcon className="mb-4" />}
                {successMessage && <Alert message={successMessage} type="success" showIcon className="mb-4" />}
                <div className="min-h-[400px]">
                    {steps[currentStep].content}
                </div>
                <div className="mt-8 flex justify-between">
                    <Button 
                        onClick={() => setCurrentStep(prev => prev - 1)} 
                        disabled={currentStep === 0}
                    >
                        Previous
                    </Button>
                    {currentStep === 0 ? (
                        <Button 
                            type="primary" 
                            onClick={() => setCurrentStep(prev => prev + 1)}
                        >
                            Next
                        </Button>
                    ) : ''}
                </div>
            </Card>
        </div>
    );
};

export default MentorshipSessionCreate;